// components/FlowDesigner.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ReactFlow,
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css'; // Import ReactFlow's default styles
import { FaSearchPlus, FaLock, FaLockOpen } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import { toast, Toaster } from 'react-hot-toast';

import { useFlow } from '../context/FlowContext/FlowContext';
import { useApi } from '../context/ApiContext/ApiContext';

import ApiAdminPage from './ApiAdminPage.jsx';
import CustomNode from './nodes/CustomNode';
import CustomEdge from './nodes/CustomEdge';
import NodeEditor from './NodeEditor';
import HeaderControls from './HeaderControls';
import FlowSelect from './FlowSelect'
import { colorOptions } from '../utils/colorOptions';

import { defaultNodeOptions, defaultNodeSettings, defaultEdgeSettings } from './fragments/nodeSettingDefaults.js';

import { exportFlow, handleFlowSelect, handleImport } from '../utils/flowUtils'; // Import utility functions

import ExportPNG from './ExportPNG'; // <--- IMPORT YOUR COMPONENT (adjust path if needed)

const nodeTypes = {
  custom: CustomNode,
  // cloudNode: CloudNode,
};

// Define edge types
const edgeTypes = {
  custom: CustomEdge, // Map 'custom' edge type to CustomEdge component
};

// INITIAL NODES
const initialNodes = [];
// INITIAL EDGES (connections between nodes)
const initialEdges = [];

// const initialFlow = '/flows/file-ingest/file-ingest-metadata.json';
const initialFlow = '';



// Flow Diagram Component
const FlowDiagram = ( { canvasColor, setCanvasColor } ) => {
  const { isLocked, setIsLocked, panMode, setPanMode, addHistoryEvent, undo, redo, undoHistory, undoPosition, updateUndoHistory, showAllModuleOverlays, setShowAllModuleOverlays } = useFlow();
  const { removeModule, resetState } = useApi();

  // Initial States
  const [ isDesign, setIsDesign ] = useState(true);
  const [ minimapState, setMinimapState ] = useState(false);
  const [ controlsState, setControlsState ] = useState(true);
  const [ gridState, setGridState ] = useState(true);
  const [ firstFlow, setFirstFlow ] = useState(initialFlow);
  const [ apiAdminPage, setApiAdminPage ] = useState(false);
  const [ notificationMessage, setNotificationMessage ] = useState(null);
  
  // General States
  // const [ isAltPressed, setIsAltPressed ] = useState(false);
  const { setViewport: setReactFlowViewport } = useReactFlow(); // Access setViewport from useReactFlow
  const [ isDragging, setIsDragging ] = useState(false);
  const [ selectedNode, setSelectedNode ] = useState(null);
  const [ selectedEdge, setSelectedEdge ] = useState(null);
  const [ nodes, setNodes, onNodesChange ] = useNodesState(initialNodes);
  const [ edges, setEdges, onEdgesChange ] = useEdgesState(initialEdges);
  const [ viewport, setViewport ] = useState({ x: 0, y: 0, zoom: 1 }); // Add viewport state
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  // General States
  const [ copiedStyle, setCopiedStyle ] = useState(null);

  // Show notification
  const showNotification = (message) => {
    // setNotificationMessage(message);
    // setTimeout(() => setNotificationMessage(null), 3000);
    toast.success(message);
  };
  
  // Track current drag state
  const [currentDrag, setCurrentDrag] = useState(null);

  // Track viewport changes
  const onMove = useCallback((event, viewport) => {
    setViewport(viewport); // Update the viewport state
  }, []);
  

  // Load Initial Flow if Provided
  useEffect(() => {
    if (initialFlow === '') return;
    
    const fetchFlows = async () => {
      try {
        const response = await fetch(initialFlow); // Path to the index file
        const data = await response.json();
        setNodes(data.nodes);
        setEdges(data.edges);
        setIsLocked(data.settings.isLocked)
        setFirstFlow(true)
        // setViewport(data.viewport); // Update the viewport state
      } catch (error) {
        console.error('Error fetching flows:', error);
      }
    };
    fetchFlows();
  }, []);

  // Fit Flow to Viewport ( This useEffect will fit the flow to view after nodes and edges are set )
  useEffect(() => {
    if (firstFlow !== '') {
      setTimeout(() => {
        reactFlowInstance.fitView();
      }, 0);
    }
  }, [firstFlow])


  // ADD NODE: Function to add a new node with default options
  const addNode = useCallback((position, nodeType='custom') => {
    if (isLocked) return;

    const newNode = {
      ...defaultNodeOptions,
      id: uuidv4(),
      position,
      type: nodeType,
      data: {
        ...defaultNodeSettings,
        label: `Node`,
        type: defaultNodeSettings.type,
        // nodeWidthHeight: nodeWidthHeight,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, isLocked]);


  // Callback function to handle connecting nodes
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        ...defaultEdgeSettings,
        type: 'custom',
        id: `e${params.source}-${params.target}`,
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  // Set Selected Node
  const onNodeClick = useCallback((event, node) => {
    console.log('Node Clicked: ', node.id, node);
    if (!isLocked) {
      event.stopPropagation(); // Prevent event bubbling
      setSelectedNode(node);
      setSelectedEdge(null);
    }
  }, [isLocked]);

  // Callback to handle edge clicks
  const onEdgeClick = useCallback((event, edge) => {
    console.log("edge", edge);
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  // Deselect Node on background click
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Update Node
  const onUpdateNode = useCallback(
    (updatedNode) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === updatedNode.id ? { ...node, data: { ...node.data, ...updatedNode.data } } : node
        )
      );
    },
    [setNodes]
  );

  // Update Edge
  const onUpdateEdge = useCallback(
    (updatedEdge) => {
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === updatedEdge.id ? { ...edge, ...updatedEdge } : edge
        )
      );
    },
    [setEdges]
  );

  // Handle Selection Changes
  // const onSelectionChange = useCallback(({ nodes, edges }) => {
  //   if (isLocked) {
  //     // When locked, we still want to show visual selection but prevent editor from opening
  //     setNodes(nds => 
  //       nds.map(node => ({
  //         ...node,
  //         selected: nodes.some(n => n.id === node.id)
  //       }))
  //     );
  //   }
  //   // } else {
  //   //   // When unlocked, allow normal selection behavior
  //   //   if (nodes.length > 0) {
  //   //     setSelectedNode(nodes[0]);
  //   //   } else {
  //   //     setSelectedNode(null);
  //   //   }
  //   // }
  // }, [isLocked, setNodes]);  

  // Delete selected edge
  const deleteSelectedEdge = useCallback(() => {
    if (selectedEdge) {
      setEdges((eds) => eds.filter((edge) => edge.id !== selectedEdge.id));
      setSelectedEdge(null);
    }
  }, [selectedEdge, setEdges]);

  // UNDO
  //  - Track node drag start
  const handleDragStart = useCallback((event, node) => {
    setSelectedNode(node);
    // console.log('dragging started');
    const source = { x: node.position.x, y: node.position.y };
    setCurrentDrag({ nodeID: node.id, source });
  }, []);
  //  - Track node drag end
  const handleDragEnd = useCallback((event, node) => {
    console.log('dragging ended');

    if (!currentDrag) return;
  
    const destination = { x: node.position.x, y: node.position.y };
  
    if (currentDrag.source.x === destination.x && 
        currentDrag.source.y === destination.y) {
      setCurrentDrag(null);
      return;
    }
  
    const newEvent = {
      type: 'position',  // Add type for different operations
      nodeID: node.id,
      source: currentDrag.source,
      destination
    };
    
    addHistoryEvent(newEvent);
    setCurrentDrag(null);
  }, [currentDrag, addHistoryEvent]);


  // Handle UNDO
  const handleUndo = useCallback(() => {
    const event = undo();
  
    if (!event) return;
  
    setNodes((nds) => {
      switch (event.type) {
        case 'position':
          return nds.map((node) =>
            node.id === event.nodeID ? { ...node, position: event.source } : node
          );
  
        case 'resize':
          return nds.map((node) =>
            node.id === event.nodeID
              ? {
                  ...node,
                  position: { ...event.source.position },
                  width: event.source.width, // Fix: Use correct properties
                  height: event.source.height // Fix: Use correct properties
                }
              : node
          );
  
  
        case 'delete':
          // Check for ID conflicts before restoring
          const existingNode = nds.find((node) => node.id === event.deletedNode.id);
          if (existingNode) {
            // Handle ID conflict: Generate a new unique ID and log the case
            const newId = uuidv4();
            console.warn(`Node ID conflict detected. Restoring node ${event.deletedNode.id} with new ID: ${newId}`);
  
            // Update the node ID in the history event
            const updatedUndoHistory = undoHistory.map(historyEvent => {
              if (historyEvent === event) {
                return {
                  ...historyEvent,
                  deletedNode: {
                    ...event.deletedNode,
                    id: newId
                  }
                };
              }
              return historyEvent;
            });
            
            // Update undoHistory and undoPosition
            updateUndoHistory(updatedUndoHistory, undoPosition);
            event.deletedNode.id = newId;
          }
  
          // Add the deleted node back to the array
          return [...nds, event.deletedNode];
  
        default:
          return nds;
      }
    });
  }, [undo, undoHistory, undoPosition, setNodes, updateUndoHistory]);
  
  // Handle REDO
  const handleRedo = useCallback(() => {
    const event = redo();
  
    if (!event) return;
  
    switch (event.type) {
      case 'position':
        setNodes((nds) =>
          nds.map((node) =>
            node.id === event.nodeID ? { ...node, position: event.destination } : node
          )
        );
        break;
  
      case 'resize':
        setNodes((nds) =>
          nds.map((node) =>
            node.id === event.nodeID
              ? { ...node,
                  position: {...event.destination.position},
                  width: event.destination.width,
                  height: event.destination.height }
              : node
          )
        );
        break;
      case 'delete':
        // Use event.nodeID (original ID) to find and remove the node
        setNodes((nds) => nds.filter((node) => node.id !== event.nodeID));
        break;
  
      default:
        break;
    }
  }, [redo, setNodes]);


  // Escape Key to deselect Nodes
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedNode(null);
        setSelectedEdge(null);
      }
      if (event.key === 'Delete' && selectedEdge) {
        deleteSelectedEdge();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdge, deleteSelectedEdge]);

  // Function to handle flow export
  const handleExport = useCallback(() => {
    exportFlow(nodes, edges, canvasColor, gridState, minimapState, controlsState, viewport, isLocked);
  }, [nodes, edges, canvasColor, gridState, minimapState, controlsState, viewport, isLocked]);


  // Store Deleted Node's State
  const handleBeforeDelete = async ({ nodes, edges }) => {
    console.log('Before Deletion:', nodes, edges);
  
    if (nodes.length > 0) {
      const deletedNode = nodes[0];
      console.log("deletedNode", deletedNode);
      console.log("deletedNode.data.feature", deletedNode.data.feature);
      const deleteEvent = {
        type: 'delete',
        nodeID: deletedNode.id,
        // Capture the entire node state for restoration
        deletedNode: {
          ...deletedNode,
        },
      };
      removeModule(deletedNode.id);
  
      addHistoryEvent(deleteEvent);
    }

    // Clear the "Selected Nodes"
    setSelectedNode(null);
    setSelectedEdge(null);
  
    return true; // Allow the deletion to proceed
  };

  // const isValidConnection = useCallback(
  //   (connection) => {
  //       // Basic check: prevent connecting a node to itself via handles
  //       if (connection.source === connection.target) {
  //          return true;
  //       }
  //       // Allow any other connection (source-source, target-target, source-target)
  //       return true;
  //   },
  //   [] // No dependencies needed for this simple version
  // );

  // Styles
  const headerStyling = {
    appHeaderContainer: "z-50 flex flex-row border-b-2 bg-gray-900 border-gray-600 text-sm text-white font-medium",
  }

  return (
    <div className=''>
      {/* Notification */}
      {notificationMessage && (
        <div className="fixed top-4 right-4 bg-indigo-600 text-white px-4 py-2 rounded shadow-lg animate-bounce duration-200 transition-opacity">
          {notificationMessage}
        </div>
      )}
      {/* <div className={`w-full ${isDesign && (selectedNode || selectedEdge) ? 'h-[500px]' : 'h-lvh'} bg-[${canvasColor}] pb-8`}> */}
      <div className={`w-full h-lvh bg-[${canvasColor}] pb-8`}>
        
        <ApiAdminPage apiAdminPage={apiAdminPage} setApiAdminPage={setApiAdminPage} showNotification={showNotification} isLocked={isLocked} />

        {/* HEADER */}
        <div className={headerStyling.appHeaderContainer}>
          <FlowSelect onFlowSelect={(flowPath) => {
            setSelectedNode(null);
            setSelectedEdge(null);
            handleFlowSelect(flowPath, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState)
          }} />

          <button className={`outline-none flex justify-center pt-1.5 min-w-10 border-r active:bg-[#4F008F] hover:text-white text-sm transition-colors duration-200 border-gray-500 ${panMode === "default" ? 'bg-[#35006C] text-gray-300' : 'bg-gray-700 border-gray-500 text-gray-300'}`}
            title={panMode === "default" ? "Switch Mouse to Pan" : "Switch Mouse to Zoom"}
            onClick={() => {setPanMode(panMode === "default" ? "scrollPan" : "default")}}
            ><FaSearchPlus />
          </button>

          <button className={`outline-none flex justify-center pt-1.5 min-w-10 border-r hover:bg-[#2c067a] active:bg-[#2c1aba] hover:text-white text-sm transition-colors duration-200 ${isLocked ? 'border-gray-500 bg-[#200457] text-gray-300' : 'border-gray-500 bg-gray-700 text-gray-300'}`}
            onClick={() => {
              setIsLocked(!isLocked);
              setSelectedNode(null);
              setSelectedEdge(null);
              setIsDesign(isLocked ? isDesign : false);
            }}
            >{isLocked ? <FaLock /> : <FaLockOpen />}
          </button>

          <button 
            onClick={() => setShowAllModuleOverlays(!showAllModuleOverlays)}
            className={`py-1 font-medium w-20 border-r hover:bg-[#2c067a] active:bg-[#2c1aba] hover:font-bold hover:text-white text-sm transition-colors duration-200 ${showAllModuleOverlays ? 'border-gray-500 bg-[#200457] text-gray-200' : 'border-gray-500 bg-gray-700 text-gray-300'}`}
            title='Show / Hide Modules'
          >
            {/* {showAllModuleOverlays ? 'Hide' : 'Show'} */}
            Modules
          </button>

          <button className={`py-1 font-medium min-w-24 border-r hover:bg-[#2c067a] active:bg-[#2c1aba] hover:font-bold hover:text-white text-sm transition-colors duration-200 ${isDesign ? 'border-gray-500 bg-[#200457] text-gray-200' : 'border-gray-500 bg-gray-700 text-gray-300'}`}
            onClick={() => {
              setIsDesign(!isDesign)
            }}
            >Designer
          </button>

          {/* INTEGRATE DownloadButton HERE */}
          <ExportPNG
            className={`${headerStyling.headerButtonInner} min-w-12 text-center border-r outline-none py-1 font-medium hover:bg-[#2c067a] active:bg-[#2c1aba] hover:font-bold hover:text-white text-sm transition-colors duration-200 border-gray-500`} // Apply consistent styling
            canvasColor={canvasColor} // Pass the current canvas color
            bgAlpha={true}
          />

          <div className='flex flex-row flex-grow'>
            {isDesign &&
            <HeaderControls
              colorOptions={colorOptions} 
              isDragging={isDragging} 
              setIsDragging={setIsDragging} 
              setSelectedNode={setSelectedNode}
              setSelectedEdge={setSelectedEdge} 
              addNode={addNode} 
              nodes={nodes} 
              setNodes={setNodes} 
              edges={edges} 
              setEdges={setEdges}
              minimapState={minimapState} 
              setMinimapState={setMinimapState} 
              controlsState={controlsState}
              setControlsState={setControlsState} 
              gridState={gridState} 
              setGridState={setGridState}
              canvasColor={canvasColor}
              setCanvasColor={setCanvasColor}
              resetState={resetState}

              onUndo={handleUndo}
              onRedo={handleRedo}
              undoHistory={undoHistory}
              undoPosition={undoPosition}

              // onImport={(file) => handleImport(file, setNodes, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance)}
              onImport={(file, option) => {
                setSelectedNode(null);
                setSelectedEdge(null);
                handleImport(file, option, nodes, setNodes, edges, setEdges, setCanvasColor, setGridState, setMinimapState, setControlsState, setReactFlowViewport, reactFlowInstance, setIsLocked, resetState)
              }}
              onExport={handleExport}  
              reactFlowInstance={reactFlowInstance}
              isLocked={isLocked}
              apiAdminPage={apiAdminPage}
              setApiAdminPage={setApiAdminPage}
            />
          }
          </div>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          // onNodesChange={isLocked ? undefined : onNodesChange}
          // onEdgesChange={isLocked ? undefined : onEdgesChange}
          // onNodesChange={(changes) => setTimeout(() => isLocked ? undefined : onNodesChange(changes))} // Timeout is needed to fix this error: "ResizeObserver loop completed with undelivered notifications."
          onNodesChange={(changes) => {
            if (isLocked) {
              // When locked, only allow selection changes
              const selectionChanges = changes.filter(change => 
                change.type === 'select' || change.type === 'unselect'
              );
              if (selectionChanges.length > 0) {
                setTimeout(() => onNodesChange(selectionChanges));
              }
              return;
            }
            // If not locked, allow all changes
            setTimeout(() => onNodesChange(changes));
          }}
        
          onEdgesChange={(changes) => setTimeout(() => isLocked ? undefined : onEdgesChange(changes))} // Timeout is needed to fix this error: "ResizeObserver loop completed with undelivered notifications."

          // onSelectionChange={onSelectionChange}

          onConnect={isLocked ? undefined : onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={isLocked ? undefined : onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          

          onBeforeDelete={handleBeforeDelete}

          nodeDragThreshold={4}
          selectNodesOnDrag={true}

          onMove={onMove} // Track viewport changes
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={viewport} // Set default viewport
          zoomOnDoubleClick={false}
  
          snapToGrid={gridState}
          snapGrid={[6, 6]}
          proOptions={{ hideAttribution: true }}
          
          nodesDraggable={!isLocked}
          nodesConnectable={!isLocked}
          elementsSelectable={true}

          // To Track Undo
          onNodeDragStart={handleDragStart}
          onNodeDragStop={handleDragEnd}

          // isValidConnection={isValidConnection}  // Added to allow any connection   // NOT WORKING       

          // Pan Scroll Mode
          panOnScroll={panMode === 'scrollPan'}
          // panOnScroll={true}
          // panOnScrollMode="free"
          // panOnScrollSpeed={0.5}
          // zoomOnScroll={panMode === 'default'}
          // preventScrolling={true}
        >
          {minimapState && <MiniMap zoomable pannable />}
          {controlsState && <Controls 
            showInteractive={false}  // Remove lock button from controls
            onInteractiveChange={(value) => {
              setIsLocked(!value);
              console.log(`Interactive status: ${!value}`);
            }}
          />}
          {gridState && <Background />}
        </ReactFlow>


      </div>

      {isDesign && (selectedNode || selectedEdge) &&
        <div className="z-[1000] fixed bottom-0 left-0 w-full border-t border-indigo-800" >
          <NodeEditor
            colorOptions={colorOptions}
            selectedNode={selectedNode}
            setSelectedNode={setSelectedNode}
            onUpdateNode={onUpdateNode}
            selectedEdge={selectedEdge}
            onUpdateEdge={onUpdateEdge}
            updateNodeInternals={updateNodeInternals}
            nodes={nodes}
            setNodes={setNodes}
            setEdges={setEdges}
            isLocked={isLocked}
            copiedStyle={copiedStyle}
            setCopiedStyle={setCopiedStyle}
          />
        </div>
      }

    </div>
  );
};

export default FlowDiagram;